import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { knowledgeApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArticleVersionHistory } from "@/components/knowledge/ArticleVersionHistory";
import { CategoryManager } from "@/components/knowledge/CategoryManager";
import { ImageManager } from "@/components/knowledge/ImageManager";
import { ArticleFormDialog } from "@/components/knowledge/ArticleFormDialog";
import { ArticleAttachments } from "@/components/knowledge/ArticleAttachments";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/SimpleToast";
import { Search, Plus, BookOpen, Edit, Trash2, User, History, FolderCog, Shield, Globe, FileText, CornerDownRight, Clock, Image as ImageIcon } from "lucide-react";

function escapeRegExp(s) {
  return String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Hebt Suchtreffer in reinem Text hervor (für die Navigation)
function Highlight({ text, query }) {
  if (!query) return <>{text}</>;
  const re = new RegExp(`(${escapeRegExp(query)})`, "ig");
  const parts = String(text).split(re);
  const q = query.toLowerCase();
  return (
    <>
      {parts.map((p, i) =>
        p.toLowerCase() === q
          ? <mark key={i} className="bg-yellow-200 text-inherit dark:bg-yellow-600/60 rounded-[2px] px-0.5">{p}</mark>
          : <span key={i}>{p}</span>
      )}
    </>
  );
}

// Hebt Suchtreffer im HTML-Inhalt hervor, ohne Tags zu zerstören
function highlightHtml(html, query) {
  if (!query) return html;
  const re = new RegExp(`(${escapeRegExp(query)})(?![^<]*>)`, "gi");
  return html.replace(re, "<mark>$1</mark>");
}

const plainOf = (a) => (a.plain_text || (a.content || "").replace(/<[^>]*>/g, " ")).toLowerCase();

export default function KnowledgeBase() {
  const toast = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [openCats, setOpenCats] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [catDrag, setCatDrag] = useState(null);
  const [imageManagerOpen, setImageManagerOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Dialoge / Formular
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [articleToDelete, setArticleToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editArticle, setEditArticle] = useState(null);
  const [formTitle, setFormTitle] = useState("");
  const [formCategory, setFormCategory] = useState("Sonstiges");
  const [formContent, setFormContent] = useState("");
  const [formTags, setFormTags] = useState("");
  const [formVisibility, setFormVisibility] = useState("all");
  const [formParentId, setFormParentId] = useState("");
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [categoryManagerOpen, setCategoryManagerOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [restoringVersion, setRestoringVersion] = useState(null);

  const canEditArticle = (article) => isAdmin || (article && article.author_id && article.author_id === user?.id);

  const fetchData = useCallback(async () => {
    try {
      const [articlesRes, categoriesRes] = await Promise.all([
        knowledgeApi.getArticles(),
        knowledgeApi.getCategories(),
      ]);
      setArticles(Array.isArray(articlesRes.data) ? articlesRes.data : []);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const q = search.trim();
  const ql = q.toLowerCase();
  const matches = useCallback((a) => !ql || a.title.toLowerCase().includes(ql) || plainOf(a).includes(ql), [ql]);

  // Baum je Kategorie: Top-Artikel (parent_id == null) + Unterartikel
  const tree = useMemo(() => {
    const byCat = {};
    const childrenOf = {};
    for (const a of articles) {
      if (a.parent_id) {
        (childrenOf[a.parent_id] = childrenOf[a.parent_id] || []).push(a);
      }
    }
    const sortFn = (x, y) => (x.order ?? 0) - (y.order ?? 0) || x.title.localeCompare(y.title);
    const cats = categories.filter((c) => articles.some((a) => a.category === c));
    for (const c of cats) {
      const tops = articles.filter((a) => a.category === c && !a.parent_id).sort(sortFn);
      const nodes = [];
      for (const top of tops) {
        const kids = (childrenOf[top.id] || []).slice().sort(sortFn);
        const topMatch = matches(top);
        const kidMatches = kids.filter(matches);
        if (!ql) {
          nodes.push({ article: top, children: kids });
        } else if (topMatch) {
          nodes.push({ article: top, children: kids });
        } else if (kidMatches.length) {
          nodes.push({ article: top, children: kidMatches });
        }
      }
      if (nodes.length) byCat[c] = nodes;
    }
    return byCat;
  }, [articles, categories, ql, matches]);

  const visibleCats = useMemo(() => Object.keys(tree), [tree]);
  const articlesById = useMemo(() => Object.fromEntries(articles.map((a) => [a.id, a])), [articles]);
  const selectedArticle = selectedId ? articlesById[selectedId] : null;

  // Auto-Auswahl beim ersten Laden
  useEffect(() => {
    if (loading || selectedId || articles.length === 0) return;
    const firstCat = categories.find((c) => articles.some((a) => a.category === c));
    const first = articles.filter((a) => a.category === firstCat && !a.parent_id).sort((x, y) => (x.order ?? 0) - (y.order ?? 0))[0] || articles[0];
    if (first) { setSelectedId(first.id); setOpenCats([first.category]); }
  }, [loading, selectedId, articles, categories]);

  // Beim Suchen alle Treffer-Kategorien aufklappen; beim Zurücksetzen Standard wiederherstellen
  useEffect(() => {
    if (ql) {
      if (visibleCats.length) setOpenCats(visibleCats);
    } else if (openCats.length === 0) {
      const cat = selectedArticle?.category || categories.find((c) => articles.some((a) => a.category === c));
      if (cat) setOpenCats([cat]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ql, visibleCats]);

  // Deep-Link aus globaler Kopfsuche: ?article={id}
  useEffect(() => {
    const aid = searchParams.get("article");
    if (!aid || loading) return;
    const art = articlesById[aid];
    if (art) {
      setSelectedId(aid);
      setOpenCats((prev) => (prev.includes(art.category) ? prev : [...prev, art.category]));
      searchParams.delete("article");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, loading, articlesById, setSearchParams]);

  const accordionValue = ql ? visibleCats : openCats;

  // Drag & Drop: Artikel innerhalb derselben Gruppe (Kategorie + Elternartikel) umsortieren
  const handleReorder = async (targetId) => {
    if (!isAdmin || !dragId || dragId === targetId) { setDragId(null); return; }
    const dragged = articlesById[dragId];
    const target = articlesById[targetId];
    setDragId(null);
    if (!dragged || !target) return;
    if (dragged.category !== target.category || (dragged.parent_id || null) !== (target.parent_id || null)) return;
    const group = articles
      .filter((a) => a.category === dragged.category && (a.parent_id || null) === (dragged.parent_id || null))
      .sort((x, y) => (x.order ?? 0) - (y.order ?? 0));
    const without = group.filter((a) => a.id !== dragId);
    const targetIdx = without.findIndex((a) => a.id === targetId);
    without.splice(targetIdx, 0, dragged);
    const items = without.map((a, i) => ({ id: a.id, order: i }));
    const orderMap = Object.fromEntries(items.map((it) => [it.id, it.order]));
    setArticles((prev) => prev.map((a) => (a.id in orderMap ? { ...a, order: orderMap[a.id] } : a)));
    try {
      await knowledgeApi.reorderArticles(items);
    } catch { toast.error("Sortierung konnte nicht gespeichert werden"); fetchData(); }
  };

  // Drag & Drop: ganze Kategorien umsortieren (nur Admin)
  const handleCategoryReorder = async (targetCat) => {
    if (!isAdmin || !catDrag || catDrag === targetCat) { setCatDrag(null); return; }
    const from = catDrag;
    setCatDrag(null);
    const list = [...categories];
    const fi = list.indexOf(from);
    const ti = list.indexOf(targetCat);
    if (fi < 0 || ti < 0) return;
    list.splice(fi, 1);
    list.splice(ti, 0, from);
    setCategories(list);
    try {
      await knowledgeApi.reorderCategories(list.map((name, i) => ({ name, order: i })));
    } catch { toast.error("Kategorie-Reihenfolge konnte nicht gespeichert werden"); fetchData(); }
  };

  const openVersionHistory = async () => {
    if (!selectedArticle) return;
    setVersionDialogOpen(true);
    setLoadingVersions(true);
    try {
      const res = await knowledgeApi.getVersions(selectedArticle.id);
      setVersions(res.data);
    } catch { toast.error("Versionen konnten nicht geladen werden"); }
    finally { setLoadingVersions(false); }
  };

  const handleRestoreVersion = async (version) => {
    if (!selectedArticle) return;
    setRestoringVersion(version);
    try {
      await knowledgeApi.restoreVersion(selectedArticle.id, version);
      toast.success(`Version ${version} wiederhergestellt`);
      setVersionDialogOpen(false);
      await fetchData();
    } catch (err) { toast.error(err?.response?.data?.detail || "Wiederherstellung fehlgeschlagen"); }
    finally { setRestoringVersion(null); }
  };

  const handleOpenDialog = (article, presetCategory) => {
    if (article) {
      setEditArticle(article);
      setFormTitle(article.title);
      setFormCategory(article.category);
      setFormContent(article.content);
      setFormTags(article.tags ? article.tags.join(", ") : "");
      setFormVisibility(article.visibility || "all");
      setFormParentId(article.parent_id || "");
    } else {
      setEditArticle(null);
      setFormTitle("");
      setFormCategory(presetCategory || visibleCats[0] || categories[0] || "Sonstiges");
      setFormContent("");
      setFormTags("");
      setFormVisibility("all");
      setFormParentId("");
    }
    setDialogOpen(true);
  };

  const parentOptions = useMemo(
    () => articles
      .filter((a) => a.category === formCategory && !a.parent_id && a.id !== editArticle?.id)
      .sort((x, y) => (x.order ?? 0) - (y.order ?? 0)),
    [articles, formCategory, editArticle]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const siblings = articles.filter((a) => a.category === formCategory && (a.parent_id || null) === (formParentId || null));
      const order = editArticle ? (editArticle.order ?? 0) : siblings.length;
      const data = {
        title: formTitle,
        category: formCategory,
        content: formContent,
        visibility: formVisibility,
        parent_id: formParentId || null,
        order,
        tags: formTags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (editArticle) {
        const res = await knowledgeApi.updateArticle(editArticle.id, data);
        toast.success("Artikel aktualisiert");
        setSelectedId(res.data.id);
      } else {
        const res = await knowledgeApi.createArticle(data);
        toast.success("Artikel erstellt");
        setSelectedId(res.data.id);
      }
      setDialogOpen(false);
      await fetchData();
    } catch { toast.error("Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!articleToDelete) return;
    try {
      await knowledgeApi.deleteArticle(articleToDelete.id);
      toast.success("Artikel gelöscht");
      if (selectedId === articleToDelete.id) setSelectedId(null);
      await fetchData();
    } catch { toast.error("Fehler beim Löschen"); }
    finally { setDeleteDialogOpen(false); setArticleToDelete(null); }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? "" : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const NavItem = ({ article, isChild }) => {
    const active = selectedId === article.id;
    return (
      <button
        type="button"
        draggable={isAdmin && !ql}
        onDragStart={() => setDragId(article.id)}
        onDragOver={(e) => { if (isAdmin && dragId) e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); handleReorder(article.id); }}
        onClick={() => setSelectedId(article.id)}
        data-testid={`kb-nav-article-${article.id}`}
        className={`w-full text-left flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${isChild ? "pl-7" : "pl-2"} ${active ? "bg-primary text-primary-foreground font-medium" : "hover:bg-muted text-foreground"} ${dragId === article.id ? "opacity-50" : ""}`}
      >
        {isChild ? <CornerDownRight className="w-3.5 h-3.5 shrink-0 opacity-60" /> : <FileText className="w-3.5 h-3.5 shrink-0 opacity-70" />}
        <span className="truncate"><Highlight text={article.title} query={q} /></span>
      </button>
    );
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="knowledge-base">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Wissensdatenbank</h1>
          <p className="text-muted-foreground mt-1">Anleitungen und Dokumentation – nur für interne Benutzer</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setImageManagerOpen(true)} data-testid="manage-images-btn">
              <ImageIcon className="w-4 h-4 mr-2" /> Bilder
            </Button>
            <Button variant="outline" onClick={() => setCategoryManagerOpen(true)} data-testid="manage-categories-btn">
              <FolderCog className="w-4 h-4 mr-2" /> Kategorien
            </Button>
            <Button onClick={() => handleOpenDialog(null, selectedArticle?.category)} data-testid="new-article-btn">
              <Plus className="w-4 h-4 mr-2" /> Neuer Artikel
            </Button>
          </div>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Volltextsuche (Titel & Inhalt)…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-11"
          data-testid="kb-search-input"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="spinner" /></div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[320px_1fr] items-start">
          {/* Linke Spalte: Kategorie-Navigation */}
          <Card className="lg:sticky lg:top-4">
            <CardContent className="p-2">
              <ScrollArea className="h-[70vh] pr-2">
                {visibleCats.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-8" data-testid="kb-nav-empty">
                    {ql ? "Keine Treffer" : "Keine Artikel"}
                  </div>
                ) : (
                  <Accordion type="multiple" value={accordionValue} onValueChange={(v) => !ql && setOpenCats(v)} className="w-full">
                    {visibleCats.map((cat) => (
                      <AccordionItem key={cat} value={cat} className="border-b-0">
                        <AccordionTrigger
                          className="px-2 py-2 hover:no-underline text-sm font-semibold"
                          data-testid={`kb-category-${cat}`}
                          draggable={isAdmin && !ql}
                          onDragStart={(e) => { setCatDrag(cat); e.stopPropagation(); }}
                          onDragOver={(e) => { if (isAdmin && catDrag) e.preventDefault(); }}
                          onDrop={(e) => { e.preventDefault(); handleCategoryReorder(cat); }}
                        >
                          <span className="text-left truncate"><Highlight text={cat} query={q} /></span>
                        </AccordionTrigger>
                        <AccordionContent className="pb-1">
                          <div className="space-y-0.5">
                            {tree[cat].map((node) => (
                              <div key={node.article.id}>
                                <NavItem article={node.article} isChild={false} />
                                {node.children.map((child) => (
                                  <NavItem key={child.id} article={child} isChild />
                                ))}
                              </div>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Rechte Spalte: Artikelinhalt */}
          <div className="min-w-0">
            {selectedArticle ? (
              <Card data-testid="kb-article-view">
                <CardContent className="p-6 space-y-4">
                  <div className="flex flex-col gap-3 border-b pb-4">
                    <div className="flex items-start justify-between gap-3">
                      <h2 className="text-2xl font-bold break-words" data-testid="kb-article-title">
                        <Highlight text={selectedArticle.title} query={q} />
                      </h2>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button variant="outline" size="sm" onClick={openVersionHistory} data-testid="version-history-btn">
                          <History className="w-4 h-4" />
                        </Button>
                        {canEditArticle(selectedArticle) && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => handleOpenDialog(selectedArticle)} data-testid="edit-article-btn">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive"
                              onClick={() => { setArticleToDelete(selectedArticle); setDeleteDialogOpen(true); }} data-testid="delete-article-btn">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                      <Badge variant="secondary">{selectedArticle.category}</Badge>
                      {selectedArticle.version > 1 && <Badge variant="outline" data-testid="article-version-badge">v{selectedArticle.version}</Badge>}
                      {selectedArticle.visibility === "admins" ? (
                        <Badge variant="outline" className="gap-1 text-amber-600 border-amber-300"><Shield className="w-3 h-3" /> Nur Admins</Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1"><Globe className="w-3 h-3" /> Alle</Badge>
                      )}
                      <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{selectedArticle.author_name || "Unbekannt"}</span>
                      <span className="flex items-center gap-1" data-testid="kb-article-updated">
                        <Clock className="w-3.5 h-3.5" /> Zuletzt aktualisiert am {formatDateTime(selectedArticle.updated_at)}
                      </span>
                    </div>
                  </div>

                  <div
                    className="prose prose-sm max-w-none"
                    data-testid="kb-article-content"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(highlightHtml(selectedArticle.content || "<p><em>Kein Inhalt.</em></p>", q)) }}
                  />

                  <ArticleAttachments articleId={selectedArticle.id} />
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-20 text-center">
                  <BookOpen className="w-16 h-16 mx-auto text-muted-foreground/30" />
                  <h3 className="text-lg font-medium mt-4">Wählen Sie links einen Artikel</h3>
                  <p className="text-muted-foreground text-sm mt-1">Nutzen Sie die Navigation oder die Volltextsuche.</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      <ArticleFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editArticle={editArticle}
        formTitle={formTitle}
        setFormTitle={setFormTitle}
        formCategory={formCategory}
        setFormCategory={setFormCategory}
        formContent={formContent}
        setFormContent={setFormContent}
        formTags={formTags}
        setFormTags={setFormTags}
        formVisibility={formVisibility}
        setFormVisibility={setFormVisibility}
        categories={categories}
        parentOptions={parentOptions}
        formParentId={formParentId}
        setFormParentId={setFormParentId}
        onSubmit={handleSubmit}
        saving={saving}
      />

      <ArticleVersionHistory
        open={versionDialogOpen}
        onOpenChange={setVersionDialogOpen}
        versions={versions}
        loading={loadingVersions}
        canEdit={selectedArticle ? canEditArticle(selectedArticle) : false}
        restoring={restoringVersion}
        onRestore={handleRestoreVersion}
      />

      <CategoryManager
        open={categoryManagerOpen}
        onOpenChange={setCategoryManagerOpen}
        categories={categories}
        onChanged={(newCats) => setCategories(newCats)}
      />

      <ImageManager open={imageManagerOpen} onOpenChange={setImageManagerOpen} />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Artikel löschen?</AlertDialogTitle>
            <AlertDialogDescription>Diese Aktion kann nicht rückgängig gemacht werden.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-article-btn">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
